import { ChangeEventHandler, FC, KeyboardEventHandler, useCallback, useMemo, useState } from 'react'
import sanitize from 'sanitize-html'
import Link from 'next/link'
import {
  CwsA,
  CwsHeaderSearch,
  CwsHeaderSearchResultList,
  CwsHeaderSearchResultListItem,
  CwsHeaderSearchResultListLink,
} from '@components/cws'
import { useRouter } from 'next/dist/client/router'
import { useDebouncedValue } from '../../../hooks/useDebounce'
import { prepareSearchTerm } from '../../../utils/slugs'
import { useMediaLess } from '../../../ui/hooks/useMediaLess'
import { device600 } from '../../../ui/hooks/useResize'
import useSWR from 'swr'
import axios from 'axios'
import { KK_LANG_IDS, Locale } from '../../../model/locales'
import { isGloballyPricelist } from '../../../utils/pricelist-utils/app-properties'
import { useTranslate } from '../../../hooks/useTranslation'
import { Suggestion } from '../../../model/misc/Suggestion'

const getBrandNameFromRawText = (text: string): { brand: string; category?: string } => {
  try {
    return JSON.parse(text)
  } catch (e) {
    return {
      brand: '',
    }
  }
}
export const HeaderSearch: FC = () => {
  const { locale, push, query } = useRouter()
  const [searchValueState, setSearchValueState] = useState('')
  const [isFocusSearch, setIsFocusSearch] = useState(false)
  const isMobile = useMediaLess(device600)
  const { translate } = useTranslate()
  const handleSearchChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchValueState(e.target?.value ?? '')
  }
  const searchValue = useDebouncedValue(searchValueState, 300)
  const searchValueUrl = useMemo(() => prepareSearchTerm(searchValue ?? ''), [searchValue])
  const canShowSearchBar = useMemo(() => {
    if (isGloballyPricelist()) {
      return Boolean(query.catalogId)
    }

    return true
  }, [query])
  const handleSearchKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>(
    async (e) => {
      const isEnterPressed = e.code?.toLowerCase() === 'enter'

      if (isEnterPressed) {
        setIsFocusSearch(false)
        if (isMobile) {
          window.location.href = query.catalogId
            ? `/${locale}/${query.catalogId}/products/${searchValueUrl}`
            : `/${locale}/products/${searchValueUrl}`
        } else {
          await push(query.catalogId ? `/${query.catalogId}/products/${searchValueUrl}` : `/products/${searchValueUrl}`)
        }
      }
    },
    [isMobile, locale, push, searchValueUrl, query]
  )
  const searchHref = query.catalogId
    ? `/${locale}/${query.catalogId}/products/${searchValueUrl}`
    : `/${locale}/products/${searchValueUrl}`

  const languageId = KK_LANG_IDS[locale as Locale]

  const { data: searchResultData } = useSWR<Suggestion[]>(
    searchValueUrl && isFocusSearch && `/api/suggestions+${searchValueUrl}`,
    async () => {
      const { data } = await axios.post(`/api/suggestions`, {
        languageId,
        languageCode: locale,
        searchText: searchValue,
      })
      return data
    }
  )

  if (!canShowSearchBar) return null
  return (
    <CwsHeaderSearch
      id="header-search"
      onChange={handleSearchChange}
      onKeyDown={handleSearchKeyDown}
      onFocus={() => {
        setIsFocusSearch(true)
      }}
      onClickOutside={() => {
        setIsFocusSearch(false)
      }}
      focus={isFocusSearch}
      onClickIcon={async () => {
        setIsFocusSearch(false)
        if (isMobile) {
          window.location.href = query.catalogId
            ? `/${locale}/${query.catalogId}/products/${searchValueUrl}`
            : `/${locale}/products/${searchValueUrl}`
        } else {
          await push(query.catalogId ? `/${query.catalogId}/products/${searchValueUrl}` : `/products/${searchValueUrl}`)
        }
      }}
    >
      {isFocusSearch && (
        <>
          <CwsHeaderSearchResultList>
            {searchResultData
              ?.filter((item) => Object.keys(JSON.parse(item.rawText)).length > 0)
              .slice(0, 3)
              .map((resultItem) => {
                const { productId, categoryId, manufacturerId } = resultItem

                let brandIdParam = ''

                if (manufacturerId > 0) {
                  const { brand } = getBrandNameFromRawText(resultItem.rawText)
                  if (brand) brandIdParam = `?brands=${brand}-${manufacturerId}`
                }

                const prefix = query.catalogId ? `/${locale}/${query.catalogId}` : `/${locale}`

                const href =
                  productId && productId > 0
                    ? `${prefix}/item/${productId}`
                    : categoryId && categoryId > 0
                    ? `${prefix}/category/${categoryId}${brandIdParam}`
                    : `${prefix}/products/${sanitize(resultItem.richText, { allowedTags: [] })}${brandIdParam}`
                return (
                  <CwsHeaderSearchResultListItem key={resultItem.id}>
                    <Link passHref href={href} legacyBehavior>
                      <CwsA hideArrow className="block" locale={locale}>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: sanitize(resultItem.richText.replace(/<b>.*?<\/b>/, `<b>${searchValue}</b>`)),
                          }}
                        />
                      </CwsA>
                    </Link>
                  </CwsHeaderSearchResultListItem>
                )
              })}
            <CwsHeaderSearchResultListLink>
              <Link href={searchHref} passHref legacyBehavior>
                <CwsA color="buoy-link" size="small" locale={locale}>
                  {translate('nav.btn.search')}
                </CwsA>
              </Link>
            </CwsHeaderSearchResultListLink>
          </CwsHeaderSearchResultList>
        </>
      )}
    </CwsHeaderSearch>
  )
}
